import { fwBold } from '@aboutyou/legacy-shared/src/ui/constants/typography'
import { exists } from '@aboutyou/legacy-shared/src/utils/exists'
import { getNewCategoryPath } from 'helpers/categoryPaths'
import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useService } from 'services/utils/context'
import { usePremiumPageStore } from 'store/context'
import ICategory from 'store/entities/Category/ICategory'
import styled from 'styled-components'
import { darkGrayColor, primaryColor } from 'uiComponents/constants/colors'
import { fontFamilyPrimary } from 'uiComponents/constants/typography'
import HorizontalSlider from 'uiComponents/molecules/Slider/HorizontalSlider'
import { IService } from '../../../services'
import { Collapsible } from './Collapsible'

const Container = styled.section`
  padding: 80px 0 0 0;
  ${fontFamilyPrimary};
`
const CatNameLinkBold = styled(Link)`
  display: block;
  ${fwBold};
  font-size: 18px;
  padding-bottom: 15px;
  color: ${primaryColor};
  &:hover {
    color: ${darkGrayColor};
  }
  sup {
    font-size: 80%;
  }
`

const CatNameLink = styled(Link)`
  display: block;
  font-size: 13px;
  padding: 5px 0;
  &:hover {
    text-decoration: underline;
  }
  sup {
    font-size: 80%;
  }
`

const Bullet = styled.span`
  padding-right: 10px;
  display: inline-block;
`

const ShowMoreButton = styled.button`
  font-size: 13px;
  &:hover {
    text-decoration: underline;
  }
`

const CategoryName: React.FC<{ categoryId: string; bold?: boolean }> = ({
  categoryId,
  bold = false,
}) => {
  const { t } = useTranslation()
  const service = useService()
  const category = service.categories.getById(categoryId)

  if (!category) {
    return null
  }

  if (bold) {
    return (
      <CatNameLinkBold
        to={getNewCategoryPath({ id: category.id, url: category.url })}
      >
        {category?.name}
      </CatNameLinkBold>
    )
  }

  return (
    <CatNameLink
      to={getNewCategoryPath({ id: category.id, url: category.url })}
    >
      <Bullet>-</Bullet>
      {category?.name}
      {category.newBadge && <sup>{t('Neu')}</sup>}
    </CatNameLink>
  )
}

const SubcategoryItem: React.FC<{ categoryId: string }> = ({ categoryId }) => {
  const service = useService()
  const category = service.categories.getById(categoryId)

  return (
    <>
      <CategoryName categoryId={categoryId} />
      {category?.childrenIds.map(id => (
        <CategoryName categoryId={id} key={id} />
      ))}
    </>
  )
}

function getSubcategoriesCount(category: ICategory, service: IService) {
  const subcategoryIds = category.childrenIds
    .map(childId => {
      const childCategory = service.categories.getById(childId)
      return [childId].concat(
        childCategory ? [...childCategory.childrenIds] : [],
      )
    })
    .flat()

  return subcategoryIds.length
}

const Subcategories: React.FC<{ category: ICategory }> = ({ category }) => {
  const service = useService()
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState(true)

  const content = (
    <>
      {category.childrenIds.map(subcatId => (
        <SubcategoryItem categoryId={subcatId} key={subcatId} />
      ))}
    </>
  )

  return getSubcategoriesCount(category, service) > 4 ? (
    <Collapsible
      openerContent={
        <ShowMoreButton>
          {t(collapsed ? 'Mehr anzeigen' : 'Weniger anzeigen')}
        </ShowMoreButton>
      }
      bodyContent={content}
      minHeight={100}
      onChange={isOpen => setCollapsed(!isOpen)}
    />
  ) : (
    content
  )
}

const PremiumTopCategoriesSlider = () => {
  const service = useService()

  const categoryId = usePremiumPageStore(store => store.categoryId)
  const category = service.categories.getById(categoryId)

  if (!category) {
    return null
  }

  const nestedCategories = category.childrenIds
    .map(id => service.categories.getById(id))
    .filter(category => category && category.childrenIds.length > 0)
    .filter(exists)

  const nestedCategoryIds = nestedCategories.map(category => category.id)

  const shallowCategoryIds = category.childrenIds.filter(
    id => !nestedCategoryIds.includes(id),
  )

  return (
    <Container>
      <HorizontalSlider
        slideCount={3}
        slideCountMedium={4}
        slideCountLarge={6}
        disableHeaderControl
      >
        {nestedCategories.map(category => {
          return (
            <div key={category.id}>
              <CategoryName bold categoryId={category.id} />
              <Subcategories category={category} />
            </div>
          )
        })}
        <div>
          {shallowCategoryIds.map(categoryId => (
            <CategoryName bold categoryId={categoryId} key={categoryId} />
          ))}
        </div>
      </HorizontalSlider>
    </Container>
  )
}

export default PremiumTopCategoriesSlider
